import React, { useState, useEffect, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import api from "../api";
import PageLayout from "../components/PageLayout";
import { useToast } from "../context/ToastContext";
import { formatDateTime } from "../utils/formatDate";

const LighthouseAudit = () => {
  const { domain } = useParams();
  const [searchParams] = useSearchParams();
  const domainId = searchParams.get("id");
  const navigate = useNavigate();
  const { success: toastSuccess, error: toastError } = useToast();

  const [loading, setLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState(0);
  const [scanResult, setScanResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [activeTab, setActiveTab] = useState("performance");
  const [auditFilter, setAuditFilter] = useState("all"); // 'all' | 'failed' | 'passed'
  // Generate Pitch removed
  const printAreaRef = useRef();

  const loadingSteps = [
    "Establishing secure handshake with Google PageSpeed cluster...",
    "Launching headless browser sandbox instance...",
    "Simulating remote network speeds (lighthouse-throttling)...",
    "Measuring Core Web Vitals (FCP, LCP, CLS, TBT)...",
    "Analyzing HTML document tree and DOM node structures...",
    "Running WCAG 2.1 AA accessibility guidelines checklist...",
    "Evaluating search engine optimization (SEO) configurations...",
    "Validating secure network headers and certificate protocols...",
    "Writing scan reports and scores to local database storage..."
  ];

  // Cycle through loading steps during an active scan
  useEffect(() => {
    let interval;
    if (loading) {
      setLoadingStep(0);
      interval = setInterval(() => {
        setLoadingStep((prev) => (prev < loadingSteps.length - 1 ? prev + 1 : prev));
      }, 4000);
    } else {
      setLoadingStep(0);
    }
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading]);

  useEffect(() => {
    if (domainId) {
      fetchHistory();
      fetchLatestReport();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domainId]);

  const fetchHistory = async () => {
    try {
      const res = await api.get(`/api/lighthouse/history/${domainId}`);
      setHistory(res.data);
    } catch (err) {
      console.error("Error loading scan history:", err);
    }
  };

  const fetchLatestReport = async () => {
    try {
      setLoading(true);
      const res = await api.get(`/api/lighthouse/history/${domainId}`);
      if (res.data.length > 0) {
        const latestDetail = await api.get(`/api/lighthouse/report/${res.data[0].id}`);
        setScanResult(latestDetail.data);
      }
    } catch (err) {
      console.error("Error fetching latest audit:", err);
    } finally {
      setLoading(false);
    }
  };

  const loadSpecificReport = async (reportId) => {
    try {
      setLoading(true);
      const res = await api.get(`/api/lighthouse/report/${reportId}`);
      setScanResult(res.data);
      toastSuccess("Loaded historical audit record");
    } catch (err) {
      toastError("Failed to load historical audit details");
    } finally {
      setLoading(false);
    }
  };

  const triggerAudit = async () => {
    if (!domainId || !domain) return;
    setLoading(true);
    setScanResult(null);

    try {
      const res = await api.post("/api/lighthouse/audit", {
        domain_id: domainId,
        domain_name: domain
      });
      setScanResult({
        ...res.data.audit,
        raw_json: res.data.raw_json
      });
      toastSuccess("Lighthouse audit completed successfully!");
      fetchHistory();
    } catch (err) {
      console.error("Audit run error:", err);
      toastError(err.response?.data?.error || "PageSpeed audit failed.");
    } finally {
      setLoading(false);
    }
  };

  const deleteLog = async (reportId) => {
    if (!window.confirm("Are you sure you want to delete this scan log?")) return;
    try {
      await api.delete(`/api/lighthouse/report/${reportId}`);
      toastSuccess("Scan log deleted successfully");
      
      if (scanResult?.id === reportId) {
        const remaining = history.filter(h => h.id !== reportId);
        if (remaining.length > 0) {
          loadSpecificReport(remaining[0].id);
        } else {
          setScanResult(null);
        }
      }
      
      fetchHistory();
    } catch (err) {
      toastError("Failed to delete scan log");
    }
  };

  const deleteAllLogs = async () => {
    if (!window.confirm("Are you sure you want to delete ALL historical scan logs for this domain? This action cannot be undone.")) return;
    try {
      await api.delete(`/api/lighthouse/history/${domainId}`);
      toastSuccess("All scan logs deleted successfully");
      setScanResult(null);
      setHistory([]);
    } catch (err) {
      toastError("Failed to clear scan history");
    }
  };

  const getScoreColor = (score) => {
    if (score >= 90) return "success";
    if (score >= 50) return "warning text-dark";
    return "danger";
  };

  const getHexColor = (score) => {
    if (score >= 90) return "#198754"; 
    if (score >= 50) return "#ffc107"; 
    return "#dc3545"; 
  };

  // Render SVG Gauge
  const ScoreGauge = ({ score, label }) => {
    const size = 100;
    const strokeWidth = 8;
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (score / 100) * circumference;
    const color = getHexColor(score);

    return (
      <div className="d-flex flex-column align-items-center p-3 text-center">
        <div style={{ position: "relative", width: size, height: size }}>
          <svg width={size} height={size} style={{ transform: "rotate(-90deg)" }}>
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke="#e9ecef"
              strokeWidth={strokeWidth}
            />
            <circle
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={color}
              strokeWidth={strokeWidth}
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              strokeLinecap="round"
              style={{ transition: "stroke-dashoffset 0.8s ease-in-out" }}
            />
          </svg>
          <div
            style={{
              position: "absolute",
              top: 0,
              left: 0,
              width: "100%",
              height: "100%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: "1.5rem",
              fontWeight: "bold",
              color: color
            }}
          >
            {score}
          </div>
        </div>
        <div className="mt-2 fw-semibold text-muted text-uppercase small">{label}</div>
      </div>
    );
  };

  const copyJsonToClipboard = () => {
    if (!scanResult?.raw_json) return;
    navigator.clipboard.writeText(JSON.stringify(scanResult.raw_json, null, 2));
    toastSuccess("JSON copied to clipboard!");
  };

  const exportAsJson = () => {
    if (!scanResult?.raw_json) return;
    const dataStr = "data:text/json;charset=utf-8," + encodeURIComponent(JSON.stringify(scanResult.raw_json, null, 2));
    const downloadAnchor = document.createElement("a");
    downloadAnchor.setAttribute("href", dataStr);
    downloadAnchor.setAttribute("download", `lighthouse-audit-${domain}-${new Date().toISOString().split('T')[0]}.json`);
    document.body.appendChild(downloadAnchor);
    downloadAnchor.click();
    downloadAnchor.remove();
    toastSuccess("JSON file downloaded!");
  };

  const exportAsPdf = async () => {
    if (!scanResult?.id) return;
    try {
      const res = await api.get(`/api/lighthouse/report/${scanResult.id}/pdf`, {
        responseType: 'blob'
      });
      const file = new Blob([res.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank');
    } catch (err) {
      console.error("PDF download failed:", err);
      toastError("Failed to generate PDF report");
    }
  };

  const exportAsPdfIssuesOnly = async () => {
    if (!scanResult?.id) return;
    try {
      const res = await api.get(`/api/lighthouse/report/${scanResult.id}/pdf`, {
        params: { issuesOnly: true },
        responseType: 'blob'
      });
      const file = new Blob([res.data], { type: 'application/pdf' });
      const fileURL = URL.createObjectURL(file);
      window.open(fileURL, '_blank');
    } catch (err) {
      console.error("PDF download failed:", err);
      toastError("Failed to generate PDF report");
    }
  };

  // Filter and extract audits for categories
  const getAuditsForCategory = (catId) => {
    if (!scanResult?.raw_json?.categories?.[catId]) return [];
    const auditRefs = scanResult.raw_json.categories[catId].auditRefs;
    const audits = scanResult.raw_json.audits;
    
    return auditRefs
      .map(ref => ({
        ...audits[ref.id],
        weight: ref.weight,
        id: ref.id
      }))
      .filter(audit => {
        if (!audit.title) return false;
        const isPassed = audit.score === null ? true : audit.score >= 0.9;
        if (auditFilter === "failed") return !isPassed;
        if (auditFilter === "passed") return isPassed;
        return true;
      });
  };

  return (
    <PageLayout title="Lighthouse & PageSpeed Audit">
      {/* Dynamic styles to preserve print colors and layout during PDF export */}
      <style>{`
        .loading-pulse {
          box-shadow: 0 0 0 0 rgba(13, 110, 253, 0.7);
          animation: loading-pulse-anim 1.6s infinite cubic-bezier(0.66, 0, 0, 1);
        }
        @keyframes loading-pulse-anim {
          to {
            box-shadow: 0 0 0 20px rgba(13, 110, 253, 0);
          }
        }
        @media print {
          /* Force colors to print */
          * {
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
            color-adjust: exact !important;
          }
          body {
            background: #ffffff !important;
            color: #212529 !important;
          }
          .no-print, .btn, .btn-group, .nav-tabs, header, footer, nav, .col-md-4.no-print {
            display: none !important;
          }
          #print-area {
            position: absolute;
            left: 0;
            top: 0;
            width: 100% !important;
            max-width: 100% !important;
            margin: 0 !important;
            padding: 0 !important;
            box-shadow: none !important;
          }
          .col-md-8 {
            width: 100% !important;
            max-width: 100% !important;
            flex: 0 0 100% !important;
          }
          .card {
            border: none !important;
            box-shadow: none !important;
            margin-bottom: 25px !important;
          }
          .card-header {
            background-color: transparent !important;
            border-bottom: 2px solid #eeeeee !important;
            padding-left: 0 !important;
            padding-right: 0 !important;
          }
          /* Ensure all category blocks display in printing mode */
          .d-print-block {
            display: block !important;
          }
          .d-print-none {
            display: none !important;
          }
        }
      `}</style>

      <div id="print-area" ref={printAreaRef}>
        <div className="row mb-4">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-body">
                <div className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                  <div>
                    <h4 className="mb-0 fw-bold">
                      Lighthouse Performance Scan:{" "}
                      <span className="text-primary">{domain}</span>
                    </h4>
                    <p className="text-muted small mb-0">Powered by Google PageSpeed Insights API</p>
                  </div>
                  <div className="d-flex gap-2 no-print">
                    <button 
                      className="btn btn-primary btn-sm px-3" 
                      onClick={triggerAudit}
                      disabled={loading}
                    >
                      <i className={`las la-sync-alt me-1 ${loading ? 'la-spin' : ''}`}></i>
                      {loading ? "Running Audit..." : "Run New Audit"}
                    </button>
                    {scanResult && (
                      <>
                        <button className="btn btn-outline-secondary btn-sm" onClick={copyJsonToClipboard} title="Copy JSON payload">
                          <i className="las la-copy me-1"></i> Copy JSON
                        </button>
                        <button className="btn btn-outline-secondary btn-sm" onClick={exportAsJson} title="Export Raw JSON File">
                          <i className="las la-download me-1"></i> Export JSON
                        </button>
                        <button className="btn btn-outline-dark btn-sm" onClick={exportAsPdf} title="Print/Export PDF">
                          <i className="las la-file-pdf me-1"></i> Save PDF
                        </button>
                        <button className="btn btn-outline-warning btn-sm" onClick={exportAsPdfIssuesOnly} title="Export PDF with Failing Issues Only">
                          <i className="las la-exclamation-triangle me-1"></i> Limited PDF
                        </button>
                      </>
                    )}
                    <button className="btn btn-outline-dark btn-sm" onClick={() => navigate("/scans")}>
                      ← Back
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Premium multi-step loading overlay */}
        {loading && (
          <div className="card border-0 shadow-sm p-5 my-4 text-center no-print bg-white">
            <div className="card-body d-flex flex-column align-items-center py-5">
              <div 
                className="loading-pulse rounded-circle bg-primary text-white d-flex align-items-center justify-content-center mb-4" 
                style={{ width: '80px', height: '80px' }}
              >
                <i className="las la-satellite-dish la-spin fs-1"></i>
              </div>
              <h4 className="fw-bold mb-2">Analyzing Domain Performance</h4>
              <p className="text-muted max-w-[500px] mb-4">
                We are performing visual performance, web accessibility, security audits, and layout diagnostics on your application.
              </p>
              
              {/* Animated Progress Bar */}
              <div className="progress w-100 max-w-[400px] mb-3" style={{ height: '8px', borderRadius: '4px' }}>
                <div 
                  className="progress-bar progress-bar-striped progress-bar-animated bg-primary" 
                  role="progressbar" 
                  style={{ 
                    width: `${((loadingStep + 1) / loadingSteps.length) * 100}%`,
                    transition: 'width 0.4s ease'
                  }}
                ></div>
              </div>
              
              {/* Dynamic Status Steps text */}
              <div className="fw-semibold text-primary animate-fade-in text-center px-3" style={{ minHeight: '24px' }}>
                {loadingSteps[loadingStep]}
              </div>
              <small className="text-muted mt-2">
                Step {loadingStep + 1} of {loadingSteps.length}
              </small>
            </div>
          </div>
        )}

        {!loading && !scanResult && (
          <div className="card text-center p-5 border-0 shadow-sm no-print">
            <div className="card-body">
              <i className="las la-chart-bar fs-1 text-muted opacity-50 mb-3" style={{ fontSize: '4rem' }}></i>
              <h5>No Audit Data Available</h5>
              <p className="text-muted">Launch a fresh audit to inspect scores, diagnostics, and performance indices.</p>
              <button className="btn btn-primary btn-lg mt-2" onClick={triggerAudit}>
                Trigger Initial Scan
              </button>
            </div>
          </div>
        )}

        {!loading && scanResult && (
          <div className="row g-4">
            {/* Score Gauges Panel */}
            <div className="col-12">
              <div className="card shadow-sm border-0">
                <div className="card-header bg-white py-3 border-light">
                  <h5 className="mb-0 fw-bold text-dark">Audit Summary</h5>
                </div>
                <div className="card-body">
                  <div className="row justify-content-around align-items-center g-3">
                    <div className="col-6 col-md-3">
                      <ScoreGauge score={scanResult.performance_score} label="Performance" />
                    </div>
                    <div className="col-6 col-md-3">
                      <ScoreGauge score={scanResult.accessibility_score} label="Accessibility" />
                    </div>
                    <div className="col-6 col-md-3">
                      <ScoreGauge score={scanResult.best_practices_score} label="Best Practices" />
                    </div>
                    <div className="col-6 col-md-3">
                      <ScoreGauge score={scanResult.seo_score} label="SEO" />
                    </div>
                  </div>
                  <div className="text-center mt-3 small text-muted">
                    Scanned on: {formatDateTime(scanResult.scanned_at)}
                  </div>
                </div>
              </div>
            </div>

            {/* Audit Details Explorer */}
            <div className="col-md-8">
              <div className="card shadow-sm border-0">
                <div className="card-header bg-white py-3 border-light d-print-none">
                  <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
                    <h5 className="mb-0 fw-bold">Diagnostic Audits</h5>
                    <div className="btn-group btn-group-sm">
                      <button 
                        className={`btn btn-${auditFilter === 'all' ? 'primary' : 'outline-secondary'}`} 
                        onClick={() => setAuditFilter('all')}
                      >
                        All
                      </button>
                      <button 
                        className={`btn btn-${auditFilter === 'failed' ? 'danger' : 'outline-secondary'}`} 
                        onClick={() => setAuditFilter('failed')}
                      >
                        Issues
                      </button>
                      <button 
                        className={`btn btn-${auditFilter === 'passed' ? 'success' : 'outline-secondary'}`} 
                        onClick={() => setAuditFilter('passed')}
                      >
                        Passed
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Category Selection Tabs (Hidden when printing) */}
                <div className="card-header bg-light p-0 border-0 d-print-none">
                  <ul className="nav nav-tabs border-0 flex-nowrap overflow-auto">
                    {["performance", "accessibility", "best-practices", "seo"].map((tab) => (
                      <li className="nav-item flex-fill text-center" key={tab}>
                        <button
                          className={`nav-link w-100 border-0 rounded-0 py-3 fw-bold text-uppercase ${
                            activeTab === tab ? "bg-white text-primary border-bottom border-primary border-3" : "text-muted"
                          }`}
                          onClick={() => setActiveTab(tab)}
                        >
                          {tab.replace("-", " ")}
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>

                <div className="card-body">
                  {/* Print / Layout Container. Render all categories. 
                      Screen: Only display activeTab. Print: display all sequentially. */}
                  {["performance", "accessibility", "best-practices", "seo"].map((categoryKey) => {
                    const isActive = activeTab === categoryKey;
                    const auditsList = getAuditsForCategory(categoryKey === 'best-practices' ? 'best-practices' : categoryKey);
                    
                    return (
                      <div 
                        key={categoryKey} 
                        className={`${isActive ? 'd-block' : 'd-none'} d-print-block mb-5`}
                      >
                        {/* Header visible only on PDF printout */}
                        <h4 className="d-none d-print-block border-bottom pb-2 mb-4 text-capitalize fw-bold text-primary">
                          {categoryKey.replace("-", " ")} Report Details
                        </h4>
                        
                        <div className="list-group list-group-flush">
                          {auditsList.length === 0 ? (
                            <div className="text-center py-4 text-muted">
                              No audit items match the filter configuration.
                            </div>
                          ) : (
                            auditsList.map((audit, index) => {
                              const isPassed = audit.score === null ? true : audit.score >= 0.9;
                              return (
                                <div className="list-group-item py-3 px-0 border-light" key={audit.id || index}>
                                  <div className="d-flex align-items-start gap-2">
                                    <span className={`badge rounded-circle p-1 bg-${isPassed ? 'success' : 'danger'} mt-1`}>
                                      <i className={`las la-${isPassed ? 'check' : 'times'} text-white`}></i>
                                    </span>
                                    <div className="flex-grow-1">
                                      <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
                                        <h6 className="mb-1 fw-bold text-dark">{audit.title}</h6>
                                        {audit.displayValue && (
                                          <span className={`badge bg-${isPassed ? 'light text-success' : 'danger bg-opacity-10 text-danger'} border`}>
                                            {audit.displayValue}
                                          </span>
                                        )}
                                      </div>
                                      <p className="text-muted small mb-0">{audit.description?.replace(/\[([^\]]+)\]\(([^)]+)\)/g, "$1")}</p>
                                    </div>
                                  </div>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Historical Scan Logs (Hidden when printing) */}
            <div className="col-md-4 no-print">
              <div className="card shadow-sm border-0">
                <div className="card-header bg-white py-3 border-light d-flex justify-content-between align-items-center">
                  <h5 className="mb-0 fw-bold">Scan Log History</h5>
                  {history.length > 0 && (
                    <button 
                      className="btn btn-sm btn-outline-danger border-0" 
                      onClick={deleteAllLogs}
                      title="Clear all logs"
                    >
                      <i className="las la-trash-alt me-1"></i> Clear All
                    </button>
                  )}
                </div>
                <div className="card-body p-0">
                  {history.length === 0 ? (
                    <div className="text-center py-4 text-muted">
                      No past scans recorded
                    </div>
                  ) : (
                    <div className="list-group list-group-flush overflow-auto" style={{ maxHeight: '450px' }}>
                      {history.map((h) => (
                        <div
                          key={h.id}
                          className={`list-group-item list-group-item-action p-3 d-flex justify-content-between align-items-center border-light ${
                            scanResult?.id === h.id ? "bg-light border-start border-primary border-3" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => loadSpecificReport(h.id)}
                        >
                          <div className="flex-grow-1">
                            <div className="d-flex justify-content-between align-items-center mb-1 me-2">
                              <span className="small fw-semibold text-dark">{formatDateTime(h.scanned_at)}</span>
                            </div>
                            <div className="d-flex gap-1">
                              <span className={`badge bg-${getScoreColor(h.performance_score)}`} title="Performance">{h.performance_score}</span>
                              <span className={`badge bg-${getScoreColor(h.accessibility_score)}`} title="Accessibility">{h.accessibility_score}</span>
                              <span className={`badge bg-${getScoreColor(h.best_practices_score)}`} title="Best Practices">{h.best_practices_score}</span>
                              <span className={`badge bg-${getScoreColor(h.seo_score)}`} title="SEO">{h.seo_score}</span>
                            </div>
                          </div>
                          <button
                            className="btn btn-sm btn-outline-danger border-0 p-1 ms-2"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteLog(h.id);
                            }}
                            title="Delete log"
                          >
                            <i className="las la-trash-alt fs-5"></i>
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

    </PageLayout>
  );
};

export default LighthouseAudit;
